// Status calculation method enum
export const statusCalculationMethodData360LiveEnum = {
  Instantaneous: "Instantaneous",
  AvgByPass: "AvgByPass", 
  AvgByZone: "AvgByZone",
  AvgByTilt: "AvgByTilt",
};

/** Lower rank = higher display priority for multi-job welds. TRUE last. */
const WELD_STATUS_PRIORITY: Record<string, number> = {
  NULL: 0,
  CROSS: 1,
  FALSE: 2,
  EX: 2, // same band as FALSE (negative); TRUE still last
  TRUE: 3,
};

const KNOWN_WELD_STATUSES = new Set(Object.keys(WELD_STATUS_PRIORITY));

/** Split job_number / wpsNumber into distinct job parts (comma/semicolon/pipe). */
export const getWeldJobParts = (jobValue: unknown): string[] => {
  if (jobValue == null || jobValue === '') return [];
  const values = Array.isArray(jobValue) ? jobValue : [jobValue];
  return values
    .flatMap((job) => String(job ?? '').split(/[,;|]/))
    .map((job) => job.trim())
    .filter(Boolean);
};

export const hasMultipleWeldJobs = (record: any): boolean => {
  const jobs = getWeldJobParts(
    record?.job_number ?? record?.jobNumber ?? record?.wpsNumber
  );
  return jobs.length > 1;
};

/** Normalize a status field into uppercase tokens (supports arrays / delimited strings). */
export const normalizeWeldStatusTokens = (status: unknown): string[] => {
  if (status == null || status === '') return [];
  if (Array.isArray(status)) {
    return status.flatMap((item) => normalizeWeldStatusTokens(item));
  }
  if (typeof status === 'boolean') {
    return [status ? 'TRUE' : 'FALSE'];
  }

  const raw = String(status).trim();
  if (!raw) return [];

  const upper = raw.toUpperCase();
  if (KNOWN_WELD_STATUSES.has(upper)) {
    return [upper];
  }

  if (/[,;|]/.test(raw)) {
    return raw
      .split(/[,;|]/)
      .map((part) => part.trim().toUpperCase())
      .filter((part) => KNOWN_WELD_STATUSES.has(part));
  }

  return [upper];
};

/**
 * Among multiple status tokens, pick priority:
 * NULL → CROSS → FALSE/EX → TRUE (thumbs up last).
 */
export const pickPriorityWeldStatus = (
  statuses: unknown
): string | null | undefined => {
  const tokens = normalizeWeldStatusTokens(statuses);
  if (tokens.length === 0) {
    return statusIsEmpty(statuses) ? (statuses as null | undefined) : undefined;
  }
  if (tokens.length === 1) {
    return tokens[0];
  }

  return tokens.reduce((best, token) => {
    const bestRank = WELD_STATUS_PRIORITY[best] ?? Number.MAX_SAFE_INTEGER;
    const tokenRank = WELD_STATUS_PRIORITY[token] ?? Number.MAX_SAFE_INTEGER;
    return tokenRank < bestRank ? token : best;
  });
};

const statusIsEmpty = (status: unknown): boolean =>
  status === null || status === undefined || status === '';

const firstDefinedStatus = (...candidates: unknown[]): unknown => {
  for (const candidate of candidates) {
    if (!statusIsEmpty(candidate)) {
      return candidate;
    }
  }
  return undefined;
};

/**
 * Resolve weld-level status. For a single job, keep legacy first-defined field behavior.
 * For multiple jobs on the same weld, prefer negative statuses (EX / FALSE) over TRUE.
 */
export const resolveWeldStatusValue = (
  record: any,
  ...fieldCandidates: unknown[]
): any => {
  if (hasMultipleWeldJobs(record)) {
    const prioritized = pickPriorityWeldStatus(fieldCandidates);
    if (prioritized !== undefined) {
      return prioritized;
    }
  }

  return firstDefinedStatus(...fieldCandidates);
};

// Get status value for Pass View table (your specific logic)
export const getPassStatus = (statusCalculationMethod: string, record: any) => {
  
  let result;
  switch (statusCalculationMethod) {
    case statusCalculationMethodData360LiveEnum.Instantaneous:
      result = record.pass_status_by_instantaneous || record.statusByInstantaneous;
      break;
    case statusCalculationMethodData360LiveEnum.AvgByPass:
      result = record.pass_avg_status || record.statusByAvgPass;
      break;
    case statusCalculationMethodData360LiveEnum.AvgByZone:
    case statusCalculationMethodData360LiveEnum.AvgByTilt:
      result = record.pass_staus_by_zone;
      break;
    default:
      result = record.pass_status_by_instantaneous || record.statusByInstantaneous;
  }
  
  return result;
};

// Get status value for Weld Status main table (correct logic)
export const getWeldStatusCalculationMethod = (statusCalculationMethod: string, record: any) => {
  switch (statusCalculationMethod) {
    case statusCalculationMethodData360LiveEnum.Instantaneous:
      return resolveWeldStatusValue(
        record,
        record.pass_status_by_instantaneous,
        record.statusByInstantaneous
      );
    case statusCalculationMethodData360LiveEnum.AvgByPass:
      return resolveWeldStatusValue(
        record,
        record.pass_avg_status,
        record.statusByAvgPass
      );
    case statusCalculationMethodData360LiveEnum.AvgByZone:
      return resolveWeldStatusValue(
        record,
        record.pass_staus_by_zone,
        record.statusByAvgZone
      );
    case statusCalculationMethodData360LiveEnum.AvgByTilt:
      return resolveWeldStatusValue(record, record.statusByAvgTilt);
    default:
      return resolveWeldStatusValue(
        record,
        record.pass_status_by_instantaneous,
        record.statusByInstantaneous
      );
  }
};

// Get status value for Zone level
export const getZoneStatus = (statusCalculationMethod: string, record: any) => {
  
  let result;
  switch (statusCalculationMethod) {
    case statusCalculationMethodData360LiveEnum.Instantaneous:
      result = record.zone_status_by_instantaneuous;
      break;
    case statusCalculationMethodData360LiveEnum.AvgByPass:
    case statusCalculationMethodData360LiveEnum.AvgByZone:
    case statusCalculationMethodData360LiveEnum.AvgByTilt:
      result = record.avg_zone_status;
      break;
    default:
      result = record.zone_status_by_instantaneuous;
  }
  
  return result;
};

// Get status value for Tilt level
export const getTiltStatus = (statusCalculationMethod: string, record: any) => {
  
  let result;
  switch (statusCalculationMethod) {
    case statusCalculationMethodData360LiveEnum.Instantaneous:
      result = record.avg_staus_by_instantaneous;
      break;
    case statusCalculationMethodData360LiveEnum.AvgByPass:
    case statusCalculationMethodData360LiveEnum.AvgByZone:
    case statusCalculationMethodData360LiveEnum.AvgByTilt:
      result = record.avg_tilt_status;
      break;
    default:
      result = record.avg_staus_by_instantaneous;
  }
  
  return result;
};

// Get status value for individual row data
export const getRowStatus = (statusCalculationMethod: string, record: any, level: 'pass' | 'zone' | 'tilt') => {
  switch (level) {
    case 'pass':
      return getPassStatus(statusCalculationMethod, record);
    case 'zone':
      return getZoneStatus(statusCalculationMethod, record);
    case 'tilt':
      return getTiltStatus(statusCalculationMethod, record);
    default:
      return record.pass_status_by_instantaneous;
  }
};

// Get status color class based on status value
export const getStatusColorClass = (status: any) => {
  if (status === null || status === undefined) return 'text-gray-400';
  
  const statusStr = String(status).toLowerCase();
  
  if (statusStr.includes('pass') || statusStr === 'true' || statusStr === '1') {
    return 'text-green-600';
  } else if (statusStr.includes('fail') || statusStr === 'false' || statusStr === '0') {
    return 'text-red-600';
  } else if (statusStr.includes('warning') || statusStr.includes('caution')) {
    return 'text-yellow-600';
  } else {
    return 'text-gray-600';
  }
};

// Get status badge class based on status value
export const getStatusBadgeClass = (status: any) => {
  if (status === null || status === undefined) return 'bg-gray-100 text-gray-600';
  
  const statusStr = String(status).toLowerCase();
  
  if (statusStr.includes('pass') || statusStr === 'true' || statusStr === '1') {
    return 'text-galvanizing-green bg-[rgba(31,154,58,0.08)]';
  } else if (statusStr.includes('fail') || statusStr === 'false' || statusStr === '0') {
    return 'text-readable-red bg-[rgba(196,0,31,0.06)]';
  } else if (statusStr.includes('warning') || statusStr.includes('caution')) {
    return 'text-[#B38600] bg-[rgba(230,161,0,0.08)]';
  } else {
    return 'bg-gray-100 text-gray-600';
  }
};
